import React, { FC, useEffect, useMemo } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Field, FormErrors, InjectedFormProps, reduxForm } from 'redux-form'
import { ConfigProps } from 'redux-form/lib/reduxForm'
import Grid from '@material-ui/core/Grid'
import { getAutocompleteOptions } from '../../planning/component/form/NouvelleCulture'
import Autocomplete from '../../../../components/form/Autocomplete'
import { FormattedMessage } from 'react-intl'
import { getAllCultures } from '../../planning/services/planningApi'
import { getStyles } from 'isotope-client'
import { MenuItem, withStyles } from '@material-ui/core'
import { getPreferences } from '../../../common/user/services/userSelectors'
import { PreviousSortieFormValues, SortieForm } from '../services/sortie.types'
import { ADD_SORTIE_FORM, FORM_FIELDS } from '../services/sortie.consts'
import Input from '../../../../components/form/Input'
import DateFieldCustom from '../../../../components/form/DateFieldCustom'
import Select from '../../../../components/form/Select'
import { Client } from '../../../common/client/client.types'
import { getClients } from '../../../common/client/client.api'
import { Culture } from '../../../common/cuture/culture.types'
import AddIcon from '@material-ui/icons/Add'
import { injectTypeConditionnementValueList } from '../../../common/valueLists/valueListInjectors'
import { formatDeboucheListFromClients } from '../services/sortie.utils'
import FormButton from '../../../../components/form/FormButton'
import { ValueListEntry } from '../../../../utils/types/global.types'

/**
 * Styles
 */
const styles = () => getStyles({
	container: {
		display: 'flex',
		flexDirection: 'row' as 'row',
		alignItems: 'center'
	},
	typeConditionnement: {
		minWidth: 150
	},
	debouche: {
		minWidth: 200
	}
})

type AddSortieFormProps = {
	idFerme: string
	typeConditionnementList: ValueListEntry[]
	classes: ClassNameMap
}

const REQUIRED_FIELDS = [
	FORM_FIELDS.CULTURE,
	FORM_FIELDS.QUANTITE,
	FORM_FIELDS.TYPE_CONDITIONNEMENT,
	FORM_FIELDS.QUANTITE_UC,
	FORM_FIELDS.DATE_SORTIE,
	FORM_FIELDS.DEBOUCHE
]

const NOW = new Date()

const validate = (values: SortieForm): FormErrors<SortieForm> => {
	const errors: FormErrors<SortieForm> = {}
	REQUIRED_FIELDS.forEach(f => {
		if (!values[f as keyof SortieForm]) {
			errors[f as keyof SortieForm] = <FormattedMessage id="global.errors.mandatory" />
		}
	})

	// Check quantités positives
	if ((values[FORM_FIELDS.QUANTITE as keyof SortieForm] ?? 0) < 0) {
		errors[FORM_FIELDS.QUANTITE as keyof SortieForm] = <FormattedMessage id="global.errors.zeroOrPositive" />
	}
	if ((values[FORM_FIELDS.QUANTITE_UC as keyof SortieForm] ?? 0) < 0) {
		errors[FORM_FIELDS.QUANTITE_UC as keyof SortieForm] = <FormattedMessage id="global.errors.zeroOrPositive" />
	}

	// Max length commentaire
	if ((values[FORM_FIELDS.COMMENTAIRE as keyof SortieForm] ?? '').toString().length > 500) {
		errors[FORM_FIELDS.COMMENTAIRE as keyof SortieForm] = <FormattedMessage id="sorties.form.commentaireSize" />
	}

	return errors
}

const AddSortieForm: FC<AddSortieFormProps & InjectedFormProps<SortieForm>> = ({ idFerme, typeConditionnementList, classes, handleSubmit }) => {
	const [cultures, setCultures] = React.useState<Culture[]>([])
	const [clients, setClients] = React.useState<Client[]>([])

	useEffect(() => {
		getClients()
			.then(setClients)
	}, [idFerme])

	useEffect(() => {
		getAllCultures(idFerme)
			.then(setCultures)
	}, [idFerme])

	const formattedDebouche = useMemo(() => formatDeboucheListFromClients(clients), [clients])

	return (
		<form className={classes.container} onSubmit={handleSubmit}>
			<Grid container>
				<Field
					name={FORM_FIELDS.CULTURE}
					component={Autocomplete}
					label={<FormattedMessage id="sorties.labels.culture" />}
					options={getAutocompleteOptions(cultures)}
					style={{ minWidth: 300 }}
					reverseBackground
				/>
				<Field
					name={FORM_FIELDS.QUANTITE}
					component={Input}
					label={<FormattedMessage id="sorties.labels.quantite" />}
					adornment="kg"
					type="number"
					style={{ maxWidth: 130 }}
					reverseBackground
				/>
				<Field
					name={FORM_FIELDS.TYPE_CONDITIONNEMENT}
					component={Select}
					label={<FormattedMessage id="sorties.form.typeConditionnement" />}
					selectClassName={classes.typeConditionnement}
					reverseBackground
				>
					{(typeConditionnementList || []).map(typeConditionnement =>
						<MenuItem key={`typeConditionnement-${typeConditionnement.code}`} value={typeConditionnement.code}>
							{typeConditionnement.label}
						</MenuItem>
					)}
				</Field>
				<Field
					name={FORM_FIELDS.QUANTITE_UC}
					component={Input}
					label={<FormattedMessage id="sorties.labels.uc" />}
					type="number"
					style={{ maxWidth: 80 }}
					reverseBackground
				/>
				<Field
					name={FORM_FIELDS.DATE_SORTIE}
					type="text"
					component={DateFieldCustom}
					label={<FormattedMessage id="sorties.labels.dateSortie" />}
					textFieldStyle={{ width: '100%' }}
					style={{ paddingLeft: 10, paddingRight: 10, minWidth: 200 }}
					withTime
					reverseBackground
				/>
				<Field
					name={FORM_FIELDS.DEBOUCHE}
					component={Select}
					label={<FormattedMessage id="sorties.labels.debouche" />}
					selectClassName={classes.debouche}
					reverseBackground
				>
					{formattedDebouche.map(debouche =>
						<MenuItem key={debouche.id} value={debouche.id}>
							{debouche.label}
						</MenuItem>
					)}
				</Field>
				<Field
					name={FORM_FIELDS.COMMENTAIRE}
					component={Input}
					label={<FormattedMessage id="sorties.labels.commentaire" />}
					style={{ minWidth: 200 }}
					reverseBackground
				/>
				<FormButton
					formName={ADD_SORTIE_FORM}
					type="secondary"
					style={{
						minWidth: 'auto',
						padding: '5px 0px 0px 0px',
						marginTop: 8,
						height: 40,
						width: 40
					}}
					keepCase
					reverseBackground
				>
					<AddIcon />
				</FormButton>
			</Grid>
		</form>
	)
}

const mapStateToProps = (state: any, { previousFormValues }: { previousFormValues: PreviousSortieFormValues | undefined }) => {
	return ({
		initialValues: {
			[FORM_FIELDS.DATE_SORTIE]: NOW,
			[FORM_FIELDS.CULTURE]: previousFormValues?.idCulture,
			[FORM_FIELDS.DEBOUCHE]: previousFormValues?.idDebouche,
			[FORM_FIELDS.TYPE_CONDITIONNEMENT]: previousFormValues?.typeConditionnement,
		},
		idFerme: (getPreferences(state).FERME_ID || '0')
	})
}

export default compose<FC<ConfigProps<SortieForm> & { previousFormValues: PreviousSortieFormValues | undefined }>>(
	connect(mapStateToProps),
	reduxForm({
		form: ADD_SORTIE_FORM,
		enableReinitialize: true,
		validate
	}),
	withStyles(styles),
	injectTypeConditionnementValueList
)(AddSortieForm)